import React, { useState } from "react";
import { api, useAuth } from "@/auth/AuthContext";
import { Card, Input, Button } from "@/components/ui-kit";
import { toast } from "sonner";
import { LockKey, EnvelopeSimple } from "@phosphor-icons/react";

const policyOk = (pw) => pw.length >= 9 && /[A-Z]/.test(pw) && /\d/.test(pw) && /[^A-Za-z0-9]/.test(pw);

const errMsg = (e, fallback) => {
  const d = e.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export const SecurityCard = ({ lang }) => {
  const { refreshUser, logout } = useAuth();
  const cs = lang === "cs";
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  const policyHint = cs
    ? "Min. 9 znaků, 1 velké písmeno, 1 číslice, 1 speciální znak"
    : "Min. 9 chars, 1 uppercase, 1 digit, 1 special character";

  const changePassword = async () => {
    if (!policyOk(newPw)) return toast.error(cs ? `Nové heslo nesplňuje pravidla: ${policyHint.toLowerCase()}` : `New password doesn't meet the policy: ${policyHint.toLowerCase()}`);
    if (newPw !== confirmPw) return toast.error(cs ? "Hesla se neshodují" : "Passwords don't match");
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success(cs ? "Heslo bylo změněno" : "Password changed");
      setCurPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) {
      toast.error(errMsg(e, cs ? "Změna hesla selhala" : "Password change failed"));
    } finally { setPwBusy(false); }
  };

  const changeEmail = async () => {
    setEmailBusy(true);
    try {
      const { data } = await api.post("/auth/change-email", { new_email: newEmail, password: emailPw });
      toast.success(cs ? `Email změněn na ${data.email}` : `Email changed to ${data.email}`);
      setNewEmail(""); setEmailPw("");
      refreshUser();
    } catch (e) {
      toast.error(errMsg(e, cs ? "Změna emailu selhala" : "Email change failed"));
    } finally { setEmailBusy(false); }
  };

  return (
    <Card className="p-6 space-y-6" data-testid="security-card">
      <h3 className="text-sm font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-2">
        <LockKey weight="bold" size={15} /> {cs ? "Zabezpečení účtu" : "Account security"}
      </h3>

      <div className="space-y-3">
        <div className="text-sm font-medium text-zinc-300">{cs ? "Změna hesla" : "Change password"}</div>
        <Input data-testid="security-current-password" type="password" label={cs ? "Současné heslo" : "Current password"} value={curPw} onChange={(e) => setCurPw(e.target.value)} />
        <Input data-testid="security-new-password" type="password" label={cs ? "Nové heslo" : "New password"} value={newPw} onChange={(e) => setNewPw(e.target.value)} />
        <Input data-testid="security-confirm-password" type="password" label={cs ? "Nové heslo znovu" : "Confirm new password"} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
        <p className="text-xs text-zinc-600">{policyHint}</p>
        <Button data-testid="security-change-password-btn" onClick={changePassword} disabled={pwBusy || !curPw || !newPw || !confirmPw}>
          {pwBusy ? "..." : (cs ? "Změnit heslo" : "Change password")}
        </Button>
      </div>

      <div className="space-y-3 border-t border-zinc-800 pt-5">
        <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
          <EnvelopeSimple weight="bold" size={15} /> {cs ? "Změna emailu" : "Change email"}
        </div>
        <Input data-testid="security-new-email" type="email" label={cs ? "Nový email" : "New email"} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="novy@email.cz" />
        <Input data-testid="security-email-password" type="password" label={cs ? "Heslo pro potvrzení" : "Password to confirm"} value={emailPw} onChange={(e) => setEmailPw(e.target.value)} />
        <Button data-testid="security-change-email-btn" onClick={changeEmail} disabled={emailBusy || !newEmail || !emailPw}>
          {emailBusy ? "..." : (cs ? "Změnit email" : "Change email")}
        </Button>
        <p className="text-xs text-zinc-600">
          {cs ? "Účty přihlašované přes Google heslo ani email měnit nemohou." : "Google sign-in accounts cannot change password or email here."}
        </p>
      </div>
    </Card>
  );
};
